import Card from "@material-ui/core/Card";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import CardTitle from "@saleor/components/CardTitle";
import Checkbox from "@saleor/components/Checkbox";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import StatusLabel from "@saleor/components/StatusLabel";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { PageheaderDetails_pageHeader } from "@saleor/pageheaders/types/PageheaderDetails";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { maybe, renderCollection } from "../../../misc";
import { ListActions, ListProps } from "../../../types";

export interface PageheaderPageTypesProps
  extends Pick<ListProps, Exclude<keyof ListProps, "onRowClick">>,
    ListActions {
  pageheader: PageheaderDetails_pageHeader;
}

const useStyles = makeStyles(
  theme => ({
    colName: {
      paddingLeft: 0,
      width: "auto"
    },
    colPublished: {
      width: 150 + theme.spacing()
    },
    colUrl: {
      width: 450
    },
    table: {
      tableLayout: "fixed"
    },
    tableRow: {
      cursor: "pointer"
    }
  }),
  { name: "PageheaderPageTypes" }
);

const numberOfColumns = 4;

const PageheaderPageTypes: React.FC<PageheaderPageTypesProps> = props => {
  const {
    pageheader,
    disabled,
    pageInfo,
    onPreviousPage,
    onNextPage,
    isChecked,
    selected,
    toggle,
    toggleAll,
    toolbar
  } = props;
  const classes = useStyles(props);

  const intl = useIntl();

  return (
    <Card>
      <CardTitle
        title={intl.formatMessage({
          defaultMessage: "Custom Pages",
          description: "section header"
        })}
      />
      <ResponsiveTable>
        <colgroup>
          <col />
          <col className={classes.colName} />
          <col className={classes.colUrl} />
          <col className={classes.colPublished} />
        </colgroup>
        <TableHead
          colSpan={numberOfColumns}
          selected={selected}
          disabled={disabled}
          items={maybe(() => pageheader.pageTypes.edges.map(edge => edge.node))}
          toggleAll={toggleAll}
          toolbar={toolbar}
        >
          <TableCell className={classes.colName}>
            <FormattedMessage defaultMessage="Name" />
          </TableCell>
          <TableCell className={classes.colUrl}>
            <FormattedMessage defaultMessage="Page Url" />
          </TableCell>
          <TableCell className={classes.colPublished}>
            <FormattedMessage
              defaultMessage="Published"
              description="custom page is published"
            />
          </TableCell>
        </TableHead>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={numberOfColumns}
              hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
              onNextPage={onNextPage}
              hasPreviousPage={
                pageInfo && !disabled ? pageInfo.hasPreviousPage : false
              }
              onPreviousPage={onPreviousPage}
            />
          </TableRow>
        </TableFooter>
        <TableBody>
          {renderCollection(
            maybe(() => pageheader.pageTypes.edges.map(edge => edge.node)),
            pageType => {
              const isSelected = pageType ? isChecked(pageType.id) : false;
              return (
                <TableRow
                  hover={!!pageType}
                  key={pageType ? pageType.id : "skeleton"}
                  className={classes.tableRow}
                  selected={isSelected}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isSelected}
                      disabled={disabled}
                      disableClickPropagation
                      onChange={() => toggle(pageType.id)}
                    />
                  </TableCell>
                  <TableCell className={classes.colName}>
                    {maybe<React.ReactNode>(
                      () => pageType.name,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell className={classes.colUrl}>
                    {maybe<React.ReactNode>(
                      () => pageType.url,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell className={classes.colPublished}>
                    <StatusLabel
                      label={
                        pageType?.publishedPage
                          ? intl.formatMessage({
                              defaultMessage: "Published",
                              description: "custom page is published"
                            })
                          : intl.formatMessage({
                              defaultMessage: "Not published",
                              description: "custom page is not published"
                            })
                      }
                      status={pageType?.publishedPage ? "success" : "error"}
                    />
                  </TableCell>
                </TableRow>
              );
            },
            () => (
              <TableRow>
                <TableCell colSpan={numberOfColumns}>
                  <FormattedMessage defaultMessage="No pagetypes found" />
                </TableCell>
              </TableRow>
            )
          )}
        </TableBody>
      </ResponsiveTable>
    </Card>
  );
};
PageheaderPageTypes.displayName = "PageheaderPageTypes";
export default PageheaderPageTypes;
